import React, { useState, useRef, useEffect, memo, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

const ChevronDown = ({ size = 16, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

const AppSelect = ({ 
  value, 
  options, 
  onChange, 
  className = '',
  dropdownClassName="",
  dropdownWidth, 
  suffixIcon,
  placement = 'left' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, bottom: 0, left: 0, right: 0, width: 0, vw: 0, vh: 0 });

  const updateCoords = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        width: rect.width,
        vw: window.innerWidth,
        vh: window.innerHeight
      });
    }
  };

  useLayoutEffect(() => {
    if (isOpen) {
      updateCoords();
      window.addEventListener('scroll', updateCoords, true);
      window.addEventListener('resize', updateCoords);
    }
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideContainer = containerRef.current && containerRef.current.contains(event.target);
      const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      
      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => 
    typeof opt === 'string' ? opt === value : opt.value === value
  );

  const displayValue = typeof selectedOption === 'string' ? selectedOption : selectedOption?.label;

  // Calculate dynamic constraints
  const maxWidth = placement === 'right' 
    ? (coords.right - 16) 
    : (coords.vw - coords.left - 16);

  const finalWidth = dropdownWidth && dropdownWidth !== 'auto'
    ? dropdownWidth
    : 'fit-content';

  const GAP = 8;
  const VIEWPORT_PADDING = 16;
  const spaceBelow = coords.vh - coords.bottom - GAP - VIEWPORT_PADDING;
  const spaceAbove = coords.top - GAP - VIEWPORT_PADDING;
  const dropUp = spaceBelow < Math.min(320, spaceAbove) && spaceAbove > spaceBelow;
  const maxHeight = Math.max(120, Math.min(320, dropUp ? spaceAbove : spaceBelow));

  return (
    <div className={`relative select-none ${className}`} ref={containerRef}>
      {/* Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full h-[32px] px-[12px] py-[6px] border border-[#ECECED] rounded-lg bg-white transition-all shadow-sm active:scale-[0.98] hover:border-[#C5C5C7]"
      >
        <div className="flex items-center flex-1 justify-start min-w-0 mr-1.5">
          <span className="text-[14px] font-medium text-[#111827] text-left truncate leading-none">
            {displayValue}
          </span>
          {suffixIcon}
        </div>
        <ChevronDown 
          size={16} 
          className={`text-[#636369] shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>


      {/* Dropdown Content via Portal */}
      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: dropUp ? 4 : -4, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: dropUp ? 4 : -4, scale: 0.95 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              style={{
                position: 'fixed',
                top: dropUp ? 'auto' : coords.bottom + GAP,
                bottom: dropUp ? (coords.vh - coords.top + GAP) : 'auto',
                left: placement === 'right' ? 'auto' : coords.left,
                right: placement === 'right' ? (coords.vw - coords.right) : 'auto',
                width: finalWidth, 
                minWidth: coords.width,
                maxWidth: maxWidth,
                zIndex: 9999,
                display: 'inline-flex',
                flexDirection: 'column'
              }}
              className={`bg-white border border-[#ECECED] rounded-xl shadow-xl p-1 ${dropdownClassName}`}
            >
              <div
                style={{ maxHeight }}
                className="overflow-y-auto auto-hide-scrollbar flex flex-col items-stretch"
              >
                {options.map((option) => {
                  const optValue = typeof option === 'string' ? option : option.value;
                  const optLabel = typeof option === 'string' ? option : option.label;
                  const optSubtitle = typeof option === 'string' ? null : option.subtitle;
                  const optLocked = typeof option === 'string' ? false : option.locked;
                  const isSelected = optValue === value;

                  const stateClass = optLocked
                    ? 'cursor-not-allowed opacity-60'
                    : isSelected
                      ? 'bg-[#F5F5F5]'
                      : 'hover:bg-[#F9FAFB]';

                  return (
                    <button
                      key={optValue}
                      disabled={optLocked}
                      onClick={() => {
                        if (optLocked) return;
                        onChange(optValue);
                        setIsOpen(false);
                      }}
                      className={`text-left p-2 rounded-lg transition-colors group mb-0.5 last:mb-0 flex flex-col items-start ${stateClass}`}
                    >
                      <div className="text-[14px] font-medium text-[#111827] leading-[20px]">
                        {optLabel}
                      </div>
                      {optSubtitle && (
                        <div className="text-[12px] text-[#636369] leading-[18px] mt-0.5">
                          {optSubtitle}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default memo(AppSelect);
